<template>
  <Dialog
    v-model="dialog"
    :title="$t('medicalProcedure.trash.title')"
    left-header-icon="icons/stop.svg"
    header-type="information"
    :buttons="buttons"
    @close="dialog = null"
  >
    <template #text>
      <div v-html="$t('medicalProcedure.trash.text')" />
    </template>
  </Dialog>
</template>

<script>
import { gql } from 'graphql-request';
import preventionData from '~/mixins/preventionData';
import { mapActions, mapGetters } from 'vuex';
import checkUserVerification from '~/mixins/checkUserVerification';
import medicalProcedures from '~/mixins/medicalProcedures.js';

export default {
  name: 'MedicalProcedureTrashDialog',
  mixins: [preventionData, checkUserVerification, medicalProcedures],
  data() {
    return {
      loading: false
    };
  },
  computed: {
    dialog: {
      get() {
        return this.$store.state.profile.dashboard.dialog === 'medicalProcedureTrash';
      },
      set(value) {
        this.$store.commit('profile/dashboard/setDialog', value);
      }
    },
    deleteEntryId() {
      return this.$route.query.eid;
    },
    ...mapGetters({
      editEntryMedicalProcedureData: 'profile/prevention/editEntryMedicalProcedureData',
      reports: 'profile/prevention/reports',
      appointments: 'profile/prevention/appointments'
    }),
    buttons() {
      return [
        {
          props: {
            color: 'tetrary'
          },
          events: {
            click: () => {
              this.dialog = null;
            }
          },
          text: this.$t('medicalProcedure.trash.keep'),
          orderMobile: 1
        },
        {
          type: 'sendButton',
          props: {
            color: 'error',
            loading: this.loading
          },
          events: {
            click: () => {
              this.deleteMedicalProcedure();
            }
          },
          text: this.$t('medicalProcedure.trash.delete'),
          orderMobile: 0
        }
      ];
    }
  },
  methods: {
    async deleteMedicalProcedure() {
      this.loading = true;

      if (!(await this.checkUserVerification(false, true))) {
        this.loading = false;
        return false;
      }

      const isAppointment =
        this.appointments.findIndex(appointment => appointment.id === this.deleteEntryId) !== -1;

      const mutationName =
        'delete' +
        `${this.capitalizeFirstLetter(this.editEntryMedicalProcedureData.type)}` +
        (isAppointment ? 'Appointment' : 'Report');

      const primaryKeyParamName = `${this.editEntryMedicalProcedureData.type}${
        isAppointment ? 'Appointment' : 'Report'
      }Id`;

      const mutation = gql`
        mutation ${mutationName}(
          $${primaryKeyParamName}: ID!,
          $profileId: ID!
        ) {
          ${mutationName}(
            ${primaryKeyParamName}: $${primaryKeyParamName},
            profileId: $profileId
          )
        }
      `;

      let variables = {
        profileId: this.$auth.user.profileId,
        [primaryKeyParamName]: this.deleteEntryId
      };

      try {
        const item = isAppointment
          ? this.appointments.find(appointment => appointment.id === this.deleteEntryId)
          : null;

        await this.$graphql.medicalProcedures.request(mutation, variables).then(async () => {
          await this.getPreventionData();
          if (isAppointment) {
            await this.loadAppointments(this.$route.query.id);
          } else {
            await this.loadReports(this.$route.query.id);
          }
          this.gtmPush({
            event: 'button_click',
            'button.type': this.$t('medicalProcedure.trash.delete'),
            'button.detail': `${this.editEntryMedicalProcedureData.title}`,
            service: 'prevence'
          });
        });

        if (item) {
          const date = new Date(item.dateTime).toISOString().split('T')[0];

          await this.sendMedicalProcedureToClinics(
            this.$auth.user.profileId,
            item.procedureInfoId,
            date,
            'delete'
          );
        }
      } catch (err) {
        this.handleError({ error: err, code: '0x00000003' });
      }

      await this.setEditEntryMedicalProcedureData({});
      this.loading = false;
      this.dialog = false;
    },
    ...mapActions({
      setEditEntryMedicalProcedureData: 'profile/prevention/setEditEntryMedicalProcedureData'
    })
  },
  created() {
    if ((this.dialog && !this.deleteEntryId) || !this.$auth.user.profileId) {
      this.dialog = false;
    }
  }
};
</script>
